import React, { Component } from 'react';
import { Card, Col, Container, Row, Spinner } from 'react-bootstrap';
// @ts-ignore
import MasterPaymentForm from '../components/PaymentForm/MasterPaymentForm';
import { RouteComponentProps } from 'react-router-dom';
import queryString from 'query-string';
import * as backendAPI from '../api/backendAPI';
import { formatDrewDate } from '../utils/utils';
import { Invoice } from '../models/Invoice';

interface State {
  payee: string;
  amount: number;
  id: string;
  companyName: string;
  impersonationToken?: string;
  paramUpdatedIfReferred: boolean,
  comment: string;
  dueDate: string;
  selectedInvoice: Invoice[] | null;
  allInvoices: Invoice[] | null;  // All invoices for the policy (for scheduling)
}

interface Params {
  id: string;
  payee: string;
  memo: string;
  amount: number;
  memo2: string;
  comment: string;
  paid_for: string;
  due_date: string;

}

const impersonationKey = process.env.REACT_APP_EPAY_IMPERSONATION_KEY;

class PaymentPage extends Component<RouteComponentProps, State> {




  state: State = {
    payee: '',
    amount: 0,
    companyName: '',
    id: '',
    paramUpdatedIfReferred: false,
    comment: '',
    dueDate: '',
    selectedInvoice: null,
    allInvoices: null
  };

  async componentDidMount() {

    /**
     * LEGACY URL FORMAT (backward compatible):
     * /pay-now?id=midwesternins&payee=Midwestern%20Insurance%20Alliance&memo2=P0014-WF231750210C%20%20_October%20P/R__Due:%2011/15/2023&memo=JOHNOLIVER%20TRUCKING%20LLC&amount=1476.30
     *
     * Legacy params:
     * - id: company identifier (ignored - we use policy number)
     * - payee: company name -> maps to paid_for/companyName
     * - memo2: contains policy number at start (before spaces/underscores)
     * - memo: payer name
     * - amount: payment amount
     *
     * NEW URL FORMAT:
     * /pay-now?policyNumber=INSTALL003&invoiceID=W2026-01&memo=Payer%20Name&paid_for=Company%20Name
     *
     * New params:
     * - policyNumber: policy number (required)
     * - invoiceID: specific invoice number (optional)
     * - memo: payer name
     * - paid_for: company name
     * - comment: payment comment
     * - due_date: due date
     */
    const params: any = queryString.parse(this.props.location.search.substring(1)) as any;

    // ========================================
    // BACKWARD COMPATIBILITY: Legacy URL params
    // ========================================

    // Extract policy number from memo2 if using legacy format
    // memo2 format: "P0014-WF231750210C  _October P/R__Due: 11/15/2023"
    // Policy number is everything before the first double-space or underscore
    if (params.memo2 && !params.policyNumber) {
      const memo2 = decodeURIComponent(params.memo2);
      const policyMatch = memo2.match(/^([A-Z0-9-]+)/i);
      if (policyMatch) {
        params.policyNumber = policyMatch[1];
      }

      // Try to extract due date from memo2 if present
      const dueDateMatch = memo2.match(/_Due:\s*(\d{1,2}\/\d{1,2}\/\d{4})/i);
      if (dueDateMatch && !params.due_date) {
        params.due_date = dueDateMatch[1];
      }
    }

    // Map legacy payee to paid_for (company name)
    if (params.payee && !params.paid_for) {
      params.paid_for = params.payee;
    }

    // Use amount from URL if no invoice is specified
    const urlAmount = params.amount ? parseFloat(params.amount) : null;

    // ========================================
    // Process params (works for both formats)
    // ========================================

    const { policyNumber, invoiceID } = params;

    // If no policy number could be determined, we can't proceed
    if (!policyNumber) {
      console.error('No policy number provided in URL params');
      this.setState({ paramUpdatedIfReferred: true });
      return;
    }

    try {
      const impersonationToken = await backendAPI.getImpersonationTokenFromPolicyNumber(policyNumber);

      // Fetch ALL invoices for this policy (needed for scheduling to work)
      let allInvoices: Invoice[] = [];
      try {
        allInvoices = await backendAPI.getInvoicesByPolicyNumber(policyNumber);
        console.log('Fetched all invoices for policy:', allInvoices);
      } catch (err) {
        console.warn('Could not fetch invoices for policy:', err);
      }

      let selectedInvoice: Invoice[] | null = null;
      let amount = urlAmount || 0;
      let dueDate = params.due_date || '';

      // If invoiceID is provided, find that specific invoice
      if (invoiceID && allInvoices.length > 0) {
        const matchedInvoice = allInvoices.find(inv => inv.InvoiceNumber === invoiceID);
        if (matchedInvoice) {
          selectedInvoice = [matchedInvoice];
          amount = Number(parseFloat(matchedInvoice.amount));
          dueDate = dueDate || formatDrewDate(matchedInvoice.dtdue);
        }
      }

      // If no specific invoice but we have invoices, auto-select the first one
      // (following sequential selection - must start from first invoice)
      if (!selectedInvoice && allInvoices.length > 0) {
        // Sort by due date to ensure we get the earliest invoice first
        const sortedInvoices = [...allInvoices].sort((a, b) => {
          return a.dtdue.localeCompare(b.dtdue);
        });

        // Auto-select the first invoice (sequential selection rule)
        selectedInvoice = [sortedInvoices[0]];
        amount = amount || Number(parseFloat(sortedInvoices[0].amount));
        dueDate = dueDate || formatDrewDate(sortedInvoices[0].dtdue);
      }

      // If we still have no invoice but have a URL amount, create a placeholder
      if (!selectedInvoice && urlAmount) {
        // No invoices found but amount provided - use URL amount directly
        amount = urlAmount;
      }

      console.log('Processed params:', { policyNumber, invoiceID, selectedInvoice, amount, allInvoices: allInvoices.length });

      await this.setState({
        impersonationToken: impersonationToken,
        id: policyNumber,
        selectedInvoice: selectedInvoice,
        allInvoices: allInvoices,
        payee: params.memo || '',
        amount: amount,
        companyName: params.paid_for || '',
        comment: params.comment || '',
        paramUpdatedIfReferred: true,
        dueDate: dueDate,
      }, () => console.log('State after URL params:', this.state));

    } catch (error) {
      console.error('Error processing URL params:', error);
      this.setState({ paramUpdatedIfReferred: true });
    }
  }


  calculateAmountFromInvoices = (invoices: Invoice[]) => {
    let amount = 0;
    invoices.forEach((invoice: Invoice) => {
      amount += parseFloat(invoice.amount);
    });
    return amount;
  };


  calculateMostRecentDateFromInvoices = (invoices: Invoice[]) => {
    var newestDatedInvoice = invoices.reduce(function (prev, current) {
      let oldDate = new Date(formatDrewDate(prev.dtdue));
      let newDate = new Date(formatDrewDate(current.dtdue));
      return oldDate > newDate ? prev : current;
    });
    return formatDrewDate(newestDatedInvoice.dtdue);
  };




  renderNewPaymentForm = () => (

    <MasterPaymentForm
      // @ts-ignore
      policyNumber={this.props.location.state.policyNumber}
      // @ts-ignore
      feinNumber={this.props.location.state.feinNumber}
      // @ts-ignore
      impersonationToken={this.props.location.state.impersonationToken}
      // @ts-ignore
      selectedInvoice={this.props.location.state.selectedInvoice}
      // @ts-ignore
      dueDate={this.calculateMostRecentDateFromInvoices(this.props.location.state.selectedInvoice)}
      // @ts-ignore
      companyName={this.props.location.state.selectedCompany.name}
      // @ts-ignore
      selectedCompany={this.props.location.state.selectedCompany}
      // @ts-ignore
      amount={this.calculateAmountFromInvoices(this.props.location.state.selectedInvoice)}
    />

  );





  renderPopulatedPaymentForm = () => {
    //passed via URL params externally

    return (
      <MasterPaymentForm
        policyNumber={this.state.id}
        impersonationToken={this.state.impersonationToken ? this.state.impersonationToken : impersonationKey!}
        selectedInvoice={this.state.selectedInvoice ? this.state.selectedInvoice : null}

        amount={this.state.amount}

        companyName={this.state.companyName}
        payee={this.state.payee}
        dueDate={this.state.dueDate}
        paymentComment={this.state.comment}

      />
    );
  };

  render() {

    return (
      <Container style={{ maxWidth: '1230px' }}>
        <Card style={{ marginBottom: '50px' }}>
          <Card.Header
            className='text-center'
            style={{
              fontSize: '28px',
              color: 'white',
              backgroundColor: '#1313b7'
            }}
          >
            Make a Payment
          </Card.Header>
          <Card.Body>
            <Row>
              <Col>

                {/* From: Paymentpage state:
                {JSON.stringify(this.state, null, 2)} */}


                {this.props.location.state ? this.renderNewPaymentForm() :

                  (this.state.paramUpdatedIfReferred && this.renderPopulatedPaymentForm())
                }

                {!this.props.location.state && !this.state.paramUpdatedIfReferred && (<Spinner
                  style={{ textAlign: 'center' }}
                  animation="border" variant="primary" />
                )}
              </Col>
            </Row>
          </Card.Body>
        </Card>
      </Container>
    );
  }
}

export default PaymentPage;
